"use client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useBulkChangeCanvasOwner,
  useQuerySearchMembers,
} from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import { AssetTypes, useNotification } from "@zapier/utils";
import { ChangeOwnerModal } from "../../common/ChangeOwnerModal";
import type { ChangeOwnerUser } from "../../common/ChangeOwnerModal/ChangeOwnerModal";

const CANVAS_PREFIX = `${AssetTypes.Canvas}:`;

interface Props {
  // Typed, prefixed asset ids (e.g. "canvas:123") from the bulk selection.
  selectedAssetIds: string[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSettled: () => void;
  folderId?: string;
}

export function CanvasBulkChangeOwnerModal({
  selectedAssetIds,
  isOpen,
  setIsOpen,
  onSettled,
  folderId,
}: Props) {
  const t = useTranslations("CanvasBulkChangeOwnerModal");
  const { createNotification } = useNotification();
  const [userSearch, setUserSearch] = useState("");
  const searchTimeoutReference = useRef<NodeJS.Timeout>();

  const { members, isLoading: isUsersLoading } = useQuerySearchMembers({
    search: userSearch,
    allowTeams: true,
  });

  const users: ChangeOwnerUser[] = useMemo(
    () =>
      members.map((member) => ({
        id: member.id,
        name: member.fullName,
        email: member.email,
        photoUrl: member.photoUrl,
      })),
    [members],
  );

  const canvasIds = useMemo(
    () =>
      selectedAssetIds.map((id) =>
        id.startsWith(CANVAS_PREFIX) ? id.slice(CANVAS_PREFIX.length) : id,
      ),
    [selectedAssetIds],
  );

  const { bulkChangeCanvasOwner } = useBulkChangeCanvasOwner({ folderId });

  const onSearch = useCallback((search: string) => {
    // Debounce to avoid excessive member-search calls.
    if (searchTimeoutReference.current) {
      clearTimeout(searchTimeoutReference.current);
    }
    searchTimeoutReference.current = setTimeout(() => {
      setUserSearch(search);
    }, 300);
  }, []);

  useEffect(() => () => clearTimeout(searchTimeoutReference.current), []);

  const onSubmit = async (ownerId: string) => {
    if (!ownerId || canvasIds.length === 0) {
      return;
    }

    try {
      const results = await bulkChangeCanvasOwner({ canvasIds, ownerId });
      const succeeded = results.filter(
        (result) => result.status === "ok",
      ).length;
      const failed = results.length - succeeded;

      if (failed === 0) {
        createNotification({
          message: t("success", { count: succeeded }),
          type: "success",
        });
      } else if (succeeded === 0) {
        createNotification({
          message: t("all-failed", { count: failed }),
          type: "error",
        });
      } else {
        createNotification({
          message: t("partial", {
            succeeded,
            total: results.length,
            failed,
          }),
          type: "warning",
        });
      }
    } catch (error) {
      createNotification({
        message: t("request-failed", {
          message: error instanceof Error ? error.message : t("unknown-error"),
        }),
        type: "error",
      });
    } finally {
      onSettled();
    }
  };

  if (!isOpen || canvasIds.length === 0) {
    return null;
  }

  const count = canvasIds.length;

  return (
    <ChangeOwnerModal
      users={users}
      id="bulk-canvas-change-owner"
      title={t("title", { count })}
      header={t("header", { count })}
      bodyText={t("body", { count })}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      assetType={AssetTypes.Canvas}
      onSearch={onSearch}
      isUsersLoading={isUsersLoading}
      onSubmit={onSubmit}
    />
  );
}
